import {
  type DocumentsSignatureProps,
  type ExternalFiles,
  useGetDocument,
  useGetDocumentsExternalFiles,
  useGetDocumentsUserRoles,
  useGetDocumentsVersions,
} from 'entities/document/api';
import { useGetDocumentAttachments } from 'entities/document/api/hooks';
import { useGetDocumentComments } from 'entities/document/api/hooks/use-get-document-comments';
import type { Attachment } from 'entities/document/api/types';
import { useGetDocumentsWorkflow } from 'entities/workflow/react/api';
import {
  marginForSignature,
  signatureHeight,
  signatureWidth,
} from '../../consts/signatureSize';
import { computedSignatures } from './compute-signatures';
import { useDocumentActions } from './useDocumentActions';
import { useDocumentCapability } from './useDocumentCapability';
import { useDocumentPages } from './useDocumentPages';

/**
 * This virtual type to represent the attachment and external file in a document.
 */
export type ComputedAttachment = {
  id: number;
  kind: 'attachment' | 'attachmentTemplates' | 'externalFile';
  name: string;
  extension?: string;
  createdAt?: string;
  updatedAt?: string;

  attachmentTemplate?: any;
  url?: string;
};

/**
 * Helper function to compute the attachment and external files
 *
 * @param attachments - The list of attachments related to the document.
 * @param externalFiles - The list of external files related to the document.
 */
const computedAttachment = (
  attachments: Attachment[] = [],
  externalFiles: ExternalFiles[] = [],
): ComputedAttachment[] => [
  ...attachments.map(attachment => ({
    ...attachment,
    attachmentTemplate: attachment.attachmentTemplate,
    id: attachment.id,
    kind: 'attachment' as const,
    name: attachment.type,
    extension: 'document',
  })),
  ...externalFiles.map(file => ({
    ...file,
    id: file.id,
    kind: 'externalFile' as const,
    name: file.name,
    extension: file.extension,
    createdAt: file.createdAt,
    updatedAt: file.updatedAt,
    url: file.downloadUrl,
  })),
];

export type UseDocumentProps = {
  /**
   * The ID of the document to be managed by the hook.
   * This is used to fetch the specific document data.
   */
  documentId?: number;

  /**
   * The ID of the version of the document to be managed by the hook.
   * This is used to fetch the specific version of the document data.
   */
  versionId?: number;

  /**
   * Optional flag to indicate whether to load attachments related to the document.
   */
  loadAttachments?: boolean;

  /**
   * Optional flag to indicate whether to load external files related to the document.
   */
  loadExternalFiles?: boolean;

  /**
   * Optional flag to indicate whether to load the workflow related to the document.
   */
  loadWorkflow?: boolean;

  /**
   * Optional flag to indicate whether to load managers related to the document.
   */
  loadManagers?: boolean;

  /**
   * Optional flag to indicate whether to load comments related to the document.
   */
  loadComments?: boolean;

  /**
   * Optional flag to indicate whether to load pages related to the document.
   */
  loadPages?: boolean;

  /**
   * Optional flag to indicate whether to load versions related to the document.
   */
  loadVersions?: boolean;
};

/**
 * Custom hook to manage document state and actions.
 * This hook is designed to handle the logic related to a specific document,
 * including fetching, updating, and managing its state.
 *
 * @param props - The properties for the hook, including the document ID.
 */
export const useDocument = (props: UseDocumentProps) => {
  /**
   * Hook to fetch a specific document by its ID.
   */
  const {
    data,
    isLoading: isLoadingDocument,
    isInitialLoading: isInitialLoadingDocument,
    isFetching: isFetchingDocument,
    refetch: refetchDocument,
  } = useGetDocument(props.documentId);

  /**
   * Hook to manage document capabilities based on the fetched document data.
   */
  const capability = useDocumentCapability({
    document: data,
  });

  /**
   * Hook to manage document actions based on the fetched document data.
   */
  const actions = useDocumentActions({
    documentId: props.documentId,
    versionId: props.versionId || data?.versionId,
  });

  /**
   * Hook to fetch external files related to the document, if specified.
   * This is only called if `loadExternalFiles` is true.
   */
  const {
    data: externalFiles,
    isInitialLoading: isInitialLoadingExternalFiles,
    isLoading: isLoadingExternalFiles,
    isFetching: isFetchingExternalFiles,
    refetch: refetchExternalFiles,
  } = useGetDocumentsExternalFiles(
    props.loadExternalFiles ? props.documentId : undefined,
  );

  /**
   * Hook to fetch attachments related to the document, if specified.
   * This is only called if `loadAttachments` is true.
   */
  const {
    data: attachments,
    isInitialLoading: isInitialLoadingAttachments,
    isLoading: isLoadingAttachments,
    isFetching: isFetchingAttachments,
    refetch: refetchAttachments,
  } = useGetDocumentAttachments(
    props.loadAttachments ? props.documentId : undefined,
  );

  /**
   * Hook to fetch the workflow related to the document, if specified.
   * This is only called if `loadWorkflow` is true.
   *
   * Gate the workflow query on the document being resolved: hasGroupsAccess is
   * derived from the document's HATEOAS links (false until `data` arrives), and
   * it is part of the workflow query key. Starting the query before the document
   * loads would fetch once with hasGroupsAccess=false, then flip to true on
   * resolution — a second cache entry plus a redundant round-trip. Waiting for
   * `data` yields a single fetch with the final value.
   */
  const {
    data: workflow,
    isInitialLoading: isInitialLoadingWorkflow,
    isLoading: isLoadingWorkflow,
    isFetching: isFetchingWorkflow,
    refetch: refetchWorkflow,
  } = useGetDocumentsWorkflow(
    props.loadWorkflow && data ? props.documentId : undefined,
    capability.queueGroups,
  );

  /**
   * Hook to fetch managers related to the document, if specified.
   * This is only called if `loadManagers` is true.
   */
  const {
    data: managers,
    isInitialLoading: isInitialLoadingManagers,
    isLoading: isLoadingManagers,
    isFetching: isFetchingManagers,
    refetch: refetchManagers,
  } = useGetDocumentsUserRoles(
    props.loadManagers ? props.documentId : undefined,
  );

  /**
   * Hook to fetch comments related to the document, if specified.
   * This is only called if `loadComments` is true.
   */
  const {
    data: comments,
    isInitialLoading: isInitialLoadingComments,
    isLoading: isLoadingComments,
    isFetching: isFetchingComments,
    refetch: refetchComments,
  } = useGetDocumentComments(props.loadComments ? props.documentId : undefined);

  const {
    data: versions,
    isInitialLoading: isInitialLoadingVersions,
    isLoading: isLoadingVersions,
    isFetching: isFetchingVersions,
    refetch: refetchVersions,
  } = useGetDocumentsVersions(
    props.loadVersions ? props.documentId : undefined,
  );

  /**
   * Hook to fetch pages related to the document, if specified.
   * This is only called if `loadPages` is true.
   */
  const {
    pages,
    isInitialLoading: isInitialLoadingPages,
    isLoading: isLoadingPages,
    isFetching: isFetchingPages,
    refetch: refetchPages,
    totalPages,
  } = useDocumentPages({
    documentId: props.loadPages ? props.documentId : undefined,
  });

  /**
   * Combines the fetched document data with external files if they are loaded.
   */
  const document = {
    ...data,
    pages,
    totalPages,
    externalFiles: props.loadExternalFiles ? externalFiles : undefined,
    attachments: props.loadAttachments ? attachments : undefined,
    computedAttachments: computedAttachment(attachments, externalFiles),
    computedSignatures: computedSignatures(data?.signatures || [], workflow),
  };

  /**
   * Boolean indicating if the document or external files are currently being fetched.
   */
  const isFetching =
    isFetchingDocument ||
    (props.loadExternalFiles ? isFetchingExternalFiles : false) ||
    (props.loadAttachments ? isFetchingAttachments : false) ||
    (props.loadWorkflow ? isFetchingWorkflow : false) ||
    (props.loadManagers ? isFetchingManagers : false) ||
    (props.loadComments ? isFetchingComments : false) ||
    (props.loadVersions ? isFetchingVersions : false) ||
    (props.loadPages ? isFetchingPages : false);

  /**
   * Boolean indicating if the document is currently being loaded.
   */
  const isLoading =
    isLoadingDocument ||
    (props.loadExternalFiles ? isLoadingExternalFiles : false) ||
    (props.loadAttachments ? isLoadingAttachments : false) ||
    (props.loadWorkflow ? isLoadingWorkflow : false) ||
    (props.loadManagers ? isLoadingManagers : false) ||
    (props.loadComments ? isLoadingComments : false) ||
    (props.loadVersions ? isLoadingVersions : false) ||
    (props.loadPages ? isLoadingPages : false);

  /**
   * Boolean indicating if the document or its related data is initially loading.
   */
  const isInitialLoading =
    isInitialLoadingDocument ||
    (props.loadExternalFiles ? isInitialLoadingExternalFiles : false) ||
    (props.loadAttachments ? isInitialLoadingAttachments : false) ||
    (props.loadWorkflow ? isInitialLoadingWorkflow : false) ||
    (props.loadManagers ? isInitialLoadingManagers : false) ||
    (props.loadComments ? isInitialLoadingComments : false) ||
    (props.loadVersions ? isInitialLoadingVersions : false) ||
    (props.loadPages ? isInitialLoadingPages : false);

  /**
   * Function to refetch the document data and external files.
   */
  const refetch = () => {
    refetchDocument();
    if (props.loadExternalFiles) {
      refetchExternalFiles();
    }

    if (props.loadAttachments) {
      refetchAttachments();
    }

    if (props.loadWorkflow) {
      refetchWorkflow();
    }

    if (props.loadManagers) {
      refetchManagers();
    }

    if (props.loadComments) {
      refetchComments();
    }

    if (props.loadVersions) {
      refetchVersions();
    }

    if (props.loadPages) {
      refetchPages();
    }
  };

  /**
   * Update the signature position of the document.
   *
   * @param position - The new signature position.
   * @param options - Additional options for the update.
   */
  const updateSignaturePosition = async (
    position: DocumentsSignatureProps,
    options: {
      documentUserId?: string;
      page?: {
        width: number;
        height: number;
      };
      signatureSize?: {
        width: number;
        height: number;
      };
      margin?: number;
    },
  ) => {
    let { x } = position;
    let { y } = position;
    const { page, margin = marginForSignature } = options;

    const signatureSize = {
      width: options.signatureSize?.width || signatureWidth,
      height: options.signatureSize?.height || signatureHeight,
    };

    const pageSize = page || pages.find(p => p.page === position.page);

    /**
     * Check if the page data is available when we try to set the position by position like 'left_bottom'
     */
    if (position.position && !pageSize) {
      // eslint-disable-next-line no-console
      console.error(
        `Not provided or not found page data for page ${position.page} in the document id ${props.documentId}`,
      );
      return;
    }

    /**
     * Determine the safe area for the signature based on the page size and signature size.
     */
    const safeArea =
      (pageSize && {
        width: pageSize.width - (signatureSize?.width || 0) - margin,
        height: pageSize.height - (signatureSize?.height || 0) - margin,
      }) ||
      undefined;

    if (position.position) {
      if (position.position.includes('top')) {
        y = margin;
      } else {
        y = safeArea?.height || margin;
      }

      if (position.position.includes('left')) {
        x = margin;
      } else {
        x = safeArea?.width || margin;
      }
    } else {
      x = Math.min(position?.x || 0, safeArea?.width || 0);
      y = Math.min(position?.y || 0, safeArea?.height || 0);
    }

    await actions.updatePositionSignature({
      documentUserId: options.documentUserId,
      page: position.page,
      x,
      y,
    });
  };

  return {
    /**
     * The document actions available for the document based on its HATEOAS links.
     */
    actions,

    /**
     * The document data fetched by the hook.
     */
    document,

    /**
     * @deprecated Use useDocumentQueue from entities/document-queue instead.
     *
     * The canonical entities/workflow `Workflow` shape (node.parameters.*,
     * participant-user/participant-group). No node-status pass runs here:
     * consumers (e.g. computedSignatures, signatures.tsx, smart-image.tsx) read
     * only participants, never node.status.
     */
    workflow,

    /**
     * Managers related to the document, if loaded.
     */
    managers,

    /**
     * Comments related to the document, if loaded.
     */
    comments: comments || {
      comments: [],
      internalComments: [],
      history: [],
    },

    /**
     * Versions related to the document, if loaded.
     */
    versions,

    /**
     * Boolean indicating if the document is initially loading.
     */
    isInitialLoading,

    /**
     * Boolean indicating if the document is currently being loaded.
     */
    isLoading,

    /**
     * Boolean indicating if the document is currently being fetched.
     */
    isFetching,

    /**
     * Function to refetch the document data.
     */
    refetch,

    /**
     * User capability available for the document based on its HATEOAS links.
     */
    capability,

    /**
     * Update the signature position of the document.
     */
    updateSignaturePosition,
  };
};

export type Document = ReturnType<typeof useDocument>['document'];
